import { Box, FormControl, FormHelperText, FormLabel, Heading, Radio, RadioGroup, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { SourcingEventDto, SourcingEventPatchDto } from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";

const TEXT_DEBOUNCE_MS = 400;

const limitedToolbar = {
  underline: false,
  headings: false,
  colors: false,
  link: false,
  table: false,
  image: false,
  template: false,
};

interface Props {
  eventId: string;
  dialoguePlanned: boolean | null;
  dialogueDescription: string | null;
}

export const DialoguePlanField = ({ eventId, dialoguePlanned, dialogueDescription }: Props) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
      <Heading as="h3" size="sm">
        {t("Dialogue planned")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mt="1">
        {t("If you plan dialogue, the procurement documents must say what the dialogue will cover (FOA § 9-3).")}
      </Text>

      <FormControl mt="4">
        <RadioGroup
          value={dialoguePlanned == null ? "" : dialoguePlanned ? "yes" : "no"}
          onChange={(value) => updateField({ publicContext: { dialoguePlanned: value === "yes" } })}
        >
          <Stack direction="row" spacing="6">
            <Radio value="yes" colorScheme="teal">
              {t("Yes")}
            </Radio>
            <Radio value="no" colorScheme="teal">
              {t("No")}
            </Radio>
          </Stack>
        </RadioGroup>
      </FormControl>

      {dialoguePlanned === true && (
        <FormControl mt="5">
          <FormLabel fontSize="sm">{t("What the dialogue will cover and how it will be carried out")}</FormLabel>
          <TipTap
            content={dialogueDescription ?? ""}
            onUpdate={(description) =>
              updateField({ publicContext: { dialogueDescription: description } }, { debounceMs: TEXT_DEBOUNCE_MS })
            }
            toolbar={limitedToolbar}
            debounceWait={0}
          />
          <FormHelperText>
            {t(
              "Shown to suppliers with the competition. Copy it into your konkurransegrunnlag as well, so the procurement documents state it.",
            )}
          </FormHelperText>
        </FormControl>
      )}
    </Box>
  );
};
